import { Helmet } from 'react-helmet'
import { useSession } from 'contexts/session-context'
import useRouter from 'hooks/use-router'
import useRedirect from 'hooks/use-redirect'
import Hero from './components/Hero'
import IntroSection from './components/IntroSection'
import WhatAreSection from './components/WhatAreSection'
import PrizesSection from './components/PrizesSection'
import HowToParticipate from './components/HowToParticipate'
import CalendarSection from './components/CalendarSection'
import BadgeVideoSection from './components/BadgeVideoSection'
import PitStopsSection from './components/PitStopsSection'
import GallerySection from './components/GallerySection'
import RankingSection from './components/RankingSection'
import F1Picture from './components/F1Picture'
import usePitStopGallery from './hooks/use-pit-stop-gallery'
import { getPitStopStatus } from './pit-stop-status'
import { F1_GALLERY_ANCHOR_ID } from './gallery'
import {
  F1_GALLERY_PHASE_ENABLED,
  F1_MOBILE_ASSETS,
  PIT_STOPS,
  PIT_STOPS_ANCHOR_ID,
  getPitStopRoute
} from './constants'

const SEO_TITLE = 'F1 Pit Stops | My Heineken'
const SEO_DESCRIPTION =
  'Heineken te lleva a la experiencia más exclusiva de F1®. Completa los Pit Stops semanales y participa por pases dobles a Foro Sol y al Grandstand.'

const RANKING_PIT_STOP = PIT_STOPS.find((pitStop) => pitStop.key === 'ranking')

const Pitstops = () => {
  const [{ user }] = useSession()
  const { push, pathname } = useRouter()
  const [, { setRedirect }] = useRedirect()

  const isLogged = user !== undefined

  // Gallery phase (from FINAL_STAGE_START): the weekly modules section is
  // replaced by the photo gallery + likes ranking, and the hero gains a
  // secondary CTA that scrolls to the gallery. Held back by the release
  // switch until the phase is approved to ship.
  const galleryPhase =
    F1_GALLERY_PHASE_ENABLED && RANKING_PIT_STOP ? getPitStopStatus(RANKING_PIT_STOP) === 'active' : false
  const gallery = usePitStopGallery(galleryPhase)

  const goToLogin = () => {
    setRedirect(pathname)
    push('/login-manager?tointernal=true')
  }

  // Visitors go register/log in (login-manager, same flow the client wired
  // for Neighbars) and come back here via internal_redirect; logged users
  // are taken to the section that is live for the current phase.
  const handleParticipate = () => {
    if (!isLogged) return goToLogin()
    const anchor = galleryPhase ? F1_GALLERY_ANCHOR_ID : PIT_STOPS_ANCHOR_ID
    document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' })
  }

  // Ranking CTA: logged users go straight to the photo rally upload
  const handleRankingParticipate = () => {
    if (!isLogged) return goToLogin()
    const route = getPitStopRoute('photo')
    if (route) push(route)
  }

  const scrollToGallery = () =>
    document.getElementById(F1_GALLERY_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' })

  return (
    <div className="pitstops">
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={encodeURI(`${F1_MOBILE_ASSETS}/hero-mobile.webp`)} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Hero />
      <IntroSection onCtaClick={handleParticipate} showGalleryCta={galleryPhase} onGalleryClick={scrollToGallery} />
      <WhatAreSection />
      {/* Single tall block (Figma 5595:12093): the diagonal-bars artwork is
          one baked background shared by prizes, steps and calendar */}
      <section className="pitstops__race">
        <F1Picture
          name="como_participar-background"
          pictureClassName="pitstops__race-bg"
          className="pitstops__race-bg-media"
        />
        <PrizesSection />
        <HowToParticipate onCtaClick={handleParticipate} />
        <CalendarSection />
      </section>
      <BadgeVideoSection />
      {galleryPhase ? (
        <>
          <GallerySection
            photos={gallery.photos}
            loading={gallery.loading}
            error={gallery.error}
            votedIds={gallery.votedIds}
            isLogged={isLogged}
            onVote={gallery.vote}
            onRequireLogin={goToLogin}
            onParticipate={handleRankingParticipate}
          />
          <RankingSection ranking={gallery.ranking} onParticipate={handleRankingParticipate} />
        </>
      ) : (
        <PitStopsSection isLogged={isLogged} onCtaClick={handleParticipate} />
      )}
    </div>
  )
}

export default Pitstops
